import React, { useState, useRef, useEffect } from 'react';

function calcWorkDays(from, to) {
  if (!from || !to) return '';
  const start = new Date(from);
  const end = new Date(to);
  if (end < start) return '';
  let count = 0;
  const d = new Date(start);
  while (d <= end) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return String(count);
}

function formatDateSK(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

const todayISO = new Date().toISOString().split('T')[0];

const EMPTY_FORM = {
  firma: '',
  priezviskoMenoTitul: '',
  osobneCislo: '',
  utvar: '',
  cisloUtvaru: '',
  rok: new Date().getFullYear().toString(),
  datumOd: '',
  datumDo: '',
  datumPodania: todayISO,
  miestoPobytu: '',
  emailKlienta: '',
};

export default function DovolenkovyListok() {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [pocetDni, setPocetDni] = useState('');
  const [hasSigned, setHasSigned] = useState(false);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  useEffect(() => {
    const days = calcWorkDays(form.datumOd, form.datumDo);
    setPocetDni(days || '');
  }, [form.datumOd, form.datumDo]);

  // Canvas init + touch event listeners (passive: false required for preventDefault)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const touch = e.touches ? e.touches[0] : e;
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    };

    const setupCtx = () => {
      ctx.strokeStyle = '#1565c0';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    const onTouchStart = (e) => {
      e.preventDefault();
      isDrawing.current = true;
      setupCtx();
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      if (!isDrawing.current) return;
      const pos = getPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      setHasSigned(true);
    };

    const onTouchEnd = () => { isDrawing.current = false; };

    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const onMouseDown = (e) => {
    isDrawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = '#1565c0';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const pos = getMousePos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const onMouseMove = (e) => {
    if (!isDrawing.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getMousePos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setHasSigned(true);
  };

  const onMouseUp = () => { isDrawing.current = false; };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!form.firma.trim()) {
      setErrorMsg('Vyplňte názov zamestnávateľa.');
      setStatus('error');
      return;
    }
    if (!form.priezviskoMenoTitul.trim()) {
      setErrorMsg('Vyplňte priezvisko, meno a titul.');
      setStatus('error');
      return;
    }
    if (!form.datumOd || !form.datumDo) {
      setErrorMsg('Zadajte dátum od a do.');
      setStatus('error');
      return;
    }
    if (new Date(form.datumDo) < new Date(form.datumOd)) {
      setErrorMsg('Dátum „do" nesmie byť pred dátumom „od".');
      setStatus('error');
      return;
    }
    if (!hasSigned) {
      setErrorMsg('Prosím, podpíšte formulár.');
      setStatus('error');
      return;
    }
    if (form.emailKlienta && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailKlienta)) {
      setErrorMsg('Zadajte platný e-mail.');
      setStatus('error');
      return;
    }

    setStatus('sending');

    const signatureDataUrl = canvasRef.current.toDataURL('image/png');

    try {
      const res = await fetch('/php/dovolenka_api.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          pocetDni,
          signature: signatureDataUrl,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setStatus('success');
      } else {
        setErrorMsg(data.error || 'Nastala chyba pri odosielaní.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Chyba pripojenia. Skúste znova.');
      setStatus('error');
    }
  };

  const resetForm = () => {
    setStatus('idle');
    setForm({ ...EMPTY_FORM });
    setPocetDni('');
    setHasSigned(false);
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }, 50);
  };

  // ── Úspech ──────────────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div style={{ background: '#f3f4f6', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '48px 40px', textAlign: 'center', maxWidth: 480, boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}>
          <div style={{ fontSize: 56, lineHeight: 1 }}>✅</div>
          <h2 style={{ color: '#1565c0', margin: '16px 0 8px' }}>Žiadosť odoslaná!</h2>
          <p style={{ color: '#555', fontSize: 15, marginBottom: 8 }}>Dovolenkový lístok bol úspešne odoslaný.</p>
          {form.emailKlienta && (
            <p style={{ color: '#888', fontSize: 13 }}>Kópia bola zaslaná na <strong>{form.emailKlienta}</strong>.</p>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24 }}>
            <button onClick={() => window.print()}
              style={{ padding: '11px 28px', background: '#fff', color: '#1565c0', border: '1.5px solid #1565c0', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              🖨 Tlačiť
            </button>
            <button onClick={resetForm}
              style={{ padding: '11px 28px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              Nová žiadosť
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Styles ──────────────────────────────────────────────────────────────────
  const BLUE = '#1565c0';
  const BORDER = `1px solid ${BLUE}`;

  const fieldInput = {
    border: 'none',
    borderBottom: `1px dotted ${BLUE}`,
    outline: 'none',
    background: 'transparent',
    fontSize: 13,
    color: '#111',
    fontFamily: 'Arial, sans-serif',
    padding: '1px 3px',
    width: '100%',
    boxSizing: 'border-box',
  };

  const cellLabel = {
    fontSize: 11,
    color: BLUE,
    fontWeight: 700,
    display: 'block',
    marginBottom: 3,
  };

  const emailInp = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #90caf9',
    borderRadius: 5,
    fontSize: 14,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div style={{ background: '#e8edf5', minHeight: '100vh', padding: '1.5rem 1rem 4rem', fontFamily: 'Arial, sans-serif' }}>

      <div id="dovolenka-print-root">
        <form onSubmit={handleSubmit}>

          {/* ── Dokument IGAZ 012 ── */}
          <div style={{ maxWidth: 720, margin: '0 auto', border: `2px solid ${BLUE}`, background: '#fff', fontFamily: 'Arial, sans-serif' }}>

            {/* Nadpis */}
            <div style={{ textAlign: 'center', padding: '10px 16px 8px', borderBottom: BORDER }}>
              <div style={{ fontSize: 26, fontWeight: 900, color: BLUE, letterSpacing: 10 }}>D O V O L E N K A</div>
            </div>

            {/* Firma */}
            <div style={{ padding: '8px 12px', borderBottom: BORDER }}>
              <label style={cellLabel}>Zamestnávateľ / Firma *</label>
              <input name="firma" value={form.firma} onChange={handleChange} style={fieldInput} placeholder="Názov firmy" />
            </div>

            {/* Priezvisko + Osobné číslo + Číslo útvaru */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', borderBottom: BORDER }}>
              <div style={{ padding: '8px 12px', borderRight: BORDER }}>
                <label style={cellLabel}>Priezvisko a meno, titul</label>
                <input name="priezviskoMenoTitul" value={form.priezviskoMenoTitul} onChange={handleChange} style={fieldInput} />
              </div>
              <div style={{ padding: '8px 12px' }}>
                <div style={{ marginBottom: 8 }}>
                  <label style={cellLabel}>Osobné číslo</label>
                  <input name="osobneCislo" value={form.osobneCislo} onChange={handleChange} style={fieldInput} />
                </div>
                <div>
                  <label style={cellLabel}>Číslo útvaru</label>
                  <input name="cisloUtvaru" value={form.cisloUtvaru} onChange={handleChange} style={fieldInput} />
                </div>
              </div>
            </div>

            {/* Útvar */}
            <div style={{ padding: '8px 12px', borderBottom: BORDER }}>
              <label style={cellLabel}>Útvar</label>
              <input name="utvar" value={form.utvar} onChange={handleChange} style={fieldInput} />
            </div>

            {/* Rok */}
            <div style={{ padding: '8px 12px', borderBottom: BORDER, fontSize: 13, color: BLUE }}>
              žiada o dovolenku na zotavenie za kalendárny rok{' '}
              <input name="rok" value={form.rok} onChange={handleChange}
                style={{ ...fieldInput, width: 70, display: 'inline-block', textAlign: 'center', fontWeight: 700 }} />
            </div>

            {/* Od – do – počet dní */}
            <div style={{ padding: '8px 12px', borderBottom: BORDER, fontSize: 13, color: BLUE, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>od</span>
              <input type="date" name="datumOd" value={form.datumOd} onChange={handleChange}
                style={{ ...fieldInput, width: 130, display: 'inline-block' }} />
              <span>do</span>
              <input type="date" name="datumDo" value={form.datumDo} onChange={handleChange}
                style={{ ...fieldInput, width: 130, display: 'inline-block' }} min={form.datumOd || undefined} />
              <span>vrátane t. j.</span>
              <input type="text" value={pocetDni} onChange={e => setPocetDni(e.target.value)}
                style={{ ...fieldInput, width: 44, display: 'inline-block', textAlign: 'center', fontWeight: 700 }} />
              <span>pracovných dní.</span>
            </div>

            {/* Miesto pobytu */}
            <div style={{ padding: '8px 12px', borderBottom: BORDER }}>
              <label style={cellLabel}>Miesto pobytu na dovolenke</label>
              <input name="miestoPobytu" value={form.miestoPobytu} onChange={handleChange} style={fieldInput} />
            </div>

            {/* Dátum + Podpis */}
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', borderBottom: BORDER }}>
              <div style={{ padding: '8px 12px', borderRight: BORDER }}>
                <label style={cellLabel}>Dátum</label>
                <input type="date" name="datumPodania" value={form.datumPodania} onChange={handleChange}
                  style={{ ...fieldInput, fontSize: 13 }} />
              </div>
              <div style={{ padding: '8px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label style={cellLabel}>Podpis zamestnanca</label>
                  <button type="button" onClick={clearSignature}
                    style={{ fontSize: 11, color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px' }}>
                    Vymazať
                  </button>
                </div>
                <div style={{ border: `1px dotted ${hasSigned ? BLUE : '#90caf9'}`, borderRadius: 3, lineHeight: 0, touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}>
                  <canvas ref={canvasRef} width={460} height={70}
                    style={{ width: '100%', height: 70, display: 'block', cursor: 'crosshair' }}
                    onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} />
                </div>
                <div style={{ fontSize: 11, color: hasSigned ? '#388e3c' : '#9e9e9e', marginTop: 3 }}>
                  {hasSigned ? '✓ Podpísané' : 'Podpíšte prstom alebo myšou'}
                </div>
              </div>
            </div>

            {/* Tabuľka schválenia */}
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '38%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '21%' }} />
                <col style={{ width: '21%' }} />
              </colgroup>
              <thead>
                <tr>
                  <td style={{ border: BORDER, padding: '4px 8px' }}></td>
                  <td style={{ border: BORDER, padding: '4px 8px', color: BLUE, fontWeight: 700, fontSize: 12, textAlign: 'center' }}>Dátum</td>
                  <td style={{ border: BORDER, padding: '4px 8px', color: BLUE, fontWeight: 700, fontSize: 12, textAlign: 'center' }}>Ved. útvaru</td>
                  <td style={{ border: BORDER, padding: '4px 8px', color: BLUE, fontWeight: 700, fontSize: 12, textAlign: 'center' }}>Pers. útvar</td>
                </tr>
              </thead>
              <tbody>
                {[
                  'Schválil',
                  'Skutočný nástup dovolenky',
                  'Nástup do zamestnania po dovolenke',
                ].map(label => (
                  <tr key={label}>
                    <td style={{ border: BORDER, padding: '4px 8px', color: BLUE, fontSize: 12 }}>{label}</td>
                    <td style={{ border: BORDER, padding: '4px 8px', height: 30 }}></td>
                    <td style={{ border: BORDER, padding: '4px 8px' }}></td>
                    <td style={{ border: BORDER, padding: '4px 8px' }}></td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} style={{ border: BORDER, padding: '5px 8px', color: BLUE, fontSize: 12 }}>
                    Z tejto dovolenky sa skutočne čerpalo _________________________ pracovných dní.
                  </td>
                </tr>
              </tbody>
            </table>

          </div>

          <div style={{ maxWidth: 720, margin: '16px auto 0', background: '#fff', border: `1px solid #90caf9`, borderRadius: 8, padding: '16px 20px' }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: BLUE, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>
                Váš e-mail – zaslať kópiu (nepovinné)
              </label>
              <input type="email" name="emailKlienta" value={form.emailKlienta} onChange={handleChange}
                style={emailInp} placeholder="vas@email.sk" autoComplete="email" />
            </div>
            {status === 'error' && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: '9px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13, border: '1px solid #ffcdd2' }}>
                ⚠️ {errorMsg}
              </div>
            )}
            <button type="submit" disabled={status === 'sending'}
              style={{
                width: '100%', padding: '12px',
                background: status === 'sending' ? '#90caf9' : BLUE,
                color: '#fff', border: 'none', borderRadius: 6, fontSize: 15, fontWeight: 600,
                cursor: status === 'sending' ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}>
              {status === 'sending' ? '⏳ Odosielam...' : '📨 Odoslať žiadosť'}
            </button>
            <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 8, textAlign: 'center' }}>
              Žiadosť bude automaticky odoslaná na ucto@uctovnictvo-poprad.sk
            </div>
          </div>

        </form>
      </div>
    </div>
  );
}
